<script setup lang="ts">
import type { InboxItem } from '@/services/notification.service'

const notificationStore = useNotificationStore()

const isLoading = computed(() => notificationStore.isMethodLoading('fetchInbox'))
const isMarkingAll = computed(() => notificationStore.isMethodLoading('markAllRead'))

const items = computed<InboxItem[]>(() => notificationStore.inbox)
const unreadCount = computed(() => notificationStore.unreadCount)

onMounted(async () => {
    try {
        await notificationStore.fetchInbox()
    } catch (e) {
        console.error(e)
    }
})

const dateFormatter = new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'medium',
    timeStyle: 'short'
})

function formatDate(value: string): string {
    return dateFormatter.format(new Date(value))
}

async function onTap(item: InboxItem) {
    if (item.gelesen) return
    try {
        await notificationStore.markRead(item.id)
    } catch (e) {
        console.error(e)
    }
}

async function markAllRead() {
    try {
        await notificationStore.markAllRead()
    } catch (e) {
        console.error(e)
    }
}

async function refresh() {
    try {
        await notificationStore.fetchInbox()
    } catch (e) {
        console.error(e)
    }
}
</script>

<template>
    <v-container fluid class="pa-0">
        <div class="d-flex align-center mb-4">
            <h1 class="text-h4 font-weight-bold mr-3">Verlauf</h1>
            <v-chip v-if="unreadCount > 0" color="primary" size="small">
                {{ unreadCount }} ungelesen
            </v-chip>
        </div>
        <p class="text-body-2 text-medium-emphasis mb-4">
            Die letzten Push-Benachrichtigungen, die Hoop für dich erzeugt hat.
        </p>

        <div class="d-flex flex-wrap ga-2 mb-4">
            <v-btn
                variant="text"
                prepend-icon="mdi-refresh"
                :loading="isLoading"
                @click="refresh"
            >
                Aktualisieren
            </v-btn>
            <v-spacer />
            <v-btn
                v-if="unreadCount > 0"
                variant="tonal"
                color="primary"
                prepend-icon="mdi-email-open"
                :loading="isMarkingAll"
                @click="markAllRead"
            >
                Alle als gelesen
            </v-btn>
        </div>

        <v-card>
            <v-card-text v-if="isLoading && items.length === 0" class="text-center">
                <v-progress-circular indeterminate />
            </v-card-text>

            <v-card-text v-else-if="items.length === 0" class="text-center text-medium-emphasis">
                <v-icon size="48" class="mb-2">mdi-inbox</v-icon>
                <div>Noch keine Benachrichtigungen.</div>
            </v-card-text>

            <v-list v-else lines="three">
                <template v-for="(item, index) in items" :key="item.id">
                    <v-list-item
                        :class="{ 'bg-blue-lighten-5': !item.gelesen }"
                        @click="onTap(item)"
                    >
                        <template #prepend>
                            <v-avatar v-if="item.pictureBase64" size="40">
                                <v-img :src="item.pictureBase64" />
                            </v-avatar>
                            <v-avatar v-else color="primary" size="40">
                                <v-icon>mdi-bell</v-icon>
                            </v-avatar>
                        </template>

                        <v-list-item-title class="font-weight-medium">
                            {{ item.titel }}
                            <v-icon v-if="!item.gelesen" size="x-small" color="primary">
                                mdi-circle
                            </v-icon>
                        </v-list-item-title>
                        <v-list-item-subtitle>
                            {{ item.nachricht }}
                        </v-list-item-subtitle>
                        <template #append>
                            <span class="text-caption text-medium-emphasis">
                                {{ formatDate(item.erstelltAm) }}
                            </span>
                        </template>
                    </v-list-item>
                    <v-divider v-if="index < items.length - 1" />
                </template>
            </v-list>
        </v-card>
    </v-container>
</template>
